"use client";

import { memo, useCallback, useState } from "react";
import { Check, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

import { trpc } from "@/lib/trpc/client";

interface AiDescriptionCardProps {
  videoId: string;
  /** Inserts the drafted markdown into the post body (TipTap). */
  onInsert: (markdown: string) => void;
}

function buildDraftMarkdown(
  description: string,
  actionPoints: string[],
): string {
  if (actionPoints.length === 0) return description;
  const bullets = actionPoints.map((point) => `- ${point}`).join("\n");
  return `${description}\n\n### Key takeaways\n\n${bullets}`;
}

/**
 * TRA-1551: adoptable AI draft (description + action points) for a library
 * video attached to a post/lesson. The draft is usually pre-generated by the
 * transcribe webhook; the button covers the not-generated-yet race and
 * explicit regeneration. Renders nothing fatal on any failure — publishing is
 * never blocked by this card.
 */
function AiDescriptionCard({
  videoId,
  onInsert,
}: AiDescriptionCardProps): React.ReactElement | null {
  const utils = trpc.useUtils();
  const [dismissed, setDismissed] = useState(false);
  const [inserted, setInserted] = useState(false);

  const { data, isLoading } = trpc.videos.getSummary.useQuery(
    { videoId },
    { enabled: !dismissed },
  );

  const generateMutation = trpc.videos.generateSummary.useMutation({
    onSuccess: (result) => {
      utils.videos.getSummary.setData({ videoId }, result);
      if (!result.summary) {
        toast.info(
          result.hasTranscript
            ? "Couldn't generate a draft — try again"
            : "Transcript is still processing — try again in a few minutes",
        );
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to generate description");
    },
  });

  const summary = data?.summary ?? null;

  const handleGenerate = useCallback(
    (force: boolean): void => {
      setInserted(false);
      generateMutation.mutate({ videoId, force });
    },
    [generateMutation, videoId],
  );

  const handleInsert = useCallback((): void => {
    if (!summary) return;
    onInsert(buildDraftMarkdown(summary.description, summary.actionPoints));
    setInserted(true);
  }, [onInsert, summary]);

  if (dismissed || isLoading) return null;

  // Generating state
  if (generateMutation.isPending) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-md bg-muted px-3 py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          Drafting description from the video transcript...
        </span>
      </div>
    );
  }

  // No draft yet — offer on-demand generation
  if (!summary) {
    return (
      <button
        type="button"
        onClick={() => handleGenerate(false)}
        className="mt-3 flex w-full items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-left transition-colors hover:border-primary/50 hover:bg-accent/50"
      >
        <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">
          Draft a description with AI from the video transcript
        </span>
      </button>
    );
  }

  // Draft ready — adoptable card
  return (
    <div className="mt-3 flex flex-col gap-2 rounded-md border border-border bg-card px-3 py-2.5">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          <Sparkles className="h-3 w-3" />
          AI draft
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleGenerate(true)}
            className="flex items-center gap-1 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
          >
            <RefreshCw className="h-3 w-3" />
            Regenerate
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss AI draft"
            className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>

      <p className="line-clamp-3 text-xs text-foreground">
        {summary.description}
      </p>

      {summary.actionPoints.length > 0 && (
        <ul className="flex list-disc flex-col gap-0.5 pl-4 text-xs text-muted-foreground">
          {summary.actionPoints.slice(0, 3).map((point) => (
            <li key={point} className="line-clamp-1">
              {point}
            </li>
          ))}
          {summary.actionPoints.length > 3 && (
            <li className="list-none text-[10px]">
              +{summary.actionPoints.length - 3} more
            </li>
          )}
        </ul>
      )}

      <button
        type="button"
        onClick={handleInsert}
        disabled={inserted}
        className="flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-default disabled:opacity-60"
      >
        {inserted ? (
          <>
            <Check className="h-3 w-3" />
            Added to post — edit freely
          </>
        ) : (
          "Use description"
        )}
      </button>
    </div>
  );
}

export default memo(AiDescriptionCard);
